import {Card} from "../../../../../../../components/card/card.jsx";
import {workScheduleSpecialCases} from "../../utils/work-schedule-utils.js";
import projectUtils from "../../../../../../../utils/project-utils.js";
import axios from "axios";
import {useEffect, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import InputForm from "../../../../../../../components/input/input-form.jsx";
import Button from "../../../../../../../components/button/button.jsx";
import {FaCalendar, FaFileExcel} from "react-icons/fa";
import clsx from "clsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../../../utils/aggrid-utils.js";
import WorkScheduleService from "../../../../../../../services/web/work-schedule-service.js";
import {translatedKeys} from "../../utils/work-schedule-i18n.js";
import {navigateHook} from "../../../../../../../hooks/use-navigate.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../../../../utils/ag-grid-columns-utils.js";
import workScheduleService from "../../../../../../../services/web/work-schedule-service.js";
import {exportObjectsToXlsx} from "../../../../../../../utils/excel-export-utils.js";


const specialCases = workScheduleSpecialCases
const WorkScheduleFormPresentation = ({id}) => {
    const gridRef = useRef(null);
    const [columnDefs, setColumnDefs] = useState([]);
    const [rowData, setRowData] = useState([]);

    const [selectedType, setSelectedType] = useState('');
    const [workSchedule, setWorkSchedule] = useState({});
    const [schedules, setSchedules] = useState({});

    const navigate = navigateHook();

    const defaultColDef = DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL;

    useQuery({
        enabled:!!id,
        queryKey: ['schedule-work-schedule'],
        queryFn: () =>  WorkScheduleService.getWorkSchedule(id)
            .then(({data}) => {
                if(data)    setWorkSchedule(data.workSchedule);
                return data ? data.workSchedule : {};
            }),
    });

    useQuery({
        enabled:!!id,
        queryKey: ['schedule-work-schedule-groups'],
        queryFn: () =>  WorkScheduleService.getWorkScheduleGroup(id)
            .then(({data}) => {
                if(data)    setSchedules(data);
                setSelectedType('effective');
                return data;
            }),
    });

    useEffect(() => {
        if (selectedType === "effective") {
            loadEffectiveData();
            setRowData(groupByEffective());
        }

        if (selectedType === "team") {
            loadTeamData();
            setRowData(groupByTeam());
        }

        if (selectedType === "day") {
            loadDateData();
            setRowData(groupByDate());
        }

    }, [selectedType]);


    const loadEffectiveData = () => {
        setColumnDefs([
            { headerName: "Efetivo",  field: "name", comparator: (a,b) => a.localeCompare(b), sortable: true,  filter: true, floatingFilter: true, width: 400 },
            { headerName: "Jornada de trabalho", field: "scale", sortable: true, filter: true, floatingFilter: true, width: 200 },
            { headerName: "Data", field: "dates", sortable: false, flex: 1, wrapText: true, autoHeight: true}
        ])
    }

    const groupByEffective = () => {
        if (!schedules?.byEffective) return [];

        return schedules?.byEffective?.map(schedule => {
            return {
                name: schedule.effective,
                scale: translatedKeys[schedule.scale] ?? schedule.scale,
                userId: schedule.effectiveId,
                dates: schedule.dates
            };
        });
    }

    const loadTeamData = () => {
        setColumnDefs([
            { headerName: "Equipe", field: "teamName", sortable: true, filter: true, floatingFilter: true, width: 400 },
            { headerName: "Jornada de trabalho", field: "scale", sortable: true, filter: true, floatingFilter: true, width: 200,
                valueFormatter: ({value}) => {
                    const foundScale = specialCases.find(scale => scale.id === value.toUpperCase())
                    return foundScale ? foundScale.name : value
                }
            },
            { headerName: "Data", field: "dates", flex: 1, sortable: false, wrapText: true, autoHeight: true, }
        ])
    }

    const groupByTeam = () => {
        if (!schedules?.byTeam) return [];

        return schedules?.byTeam?.map(schedule => {
            return {
                teamName: schedule.team,
                scale: translatedKeys[schedule.scale] ?? schedule.scale,
                dates: schedule.dates
            };
        });
    }

    const loadDateData = () => {
        setColumnDefs([
            { headerName: "Data", field: "day", sortable: true, filter: true, floatingFilter: true, width: 200},
            { headerName: "Equipe", field: "teamName", sortable: true, filter: true, floatingFilter: true, wrapText: true, autoHeight: true },
            { headerName: "Efetivo", field: "agents", flex: 1, wrapText: true, autoHeight: true, comparator: (a,b) => a.localeCompare(b), sortable: true, filter: true, floatingFilter: true },
            { headerName: "Jornada de trabalho", field: "scales", sortable: true, filter: true, floatingFilter: true, wrapText: true, autoHeight: true,
                valueFormatter: ({value}) => {
                    const foundScale = specialCases.find(scale => scale.id === value.toUpperCase())
                    return foundScale ? foundScale.name : value
                }
            }
        ]);
    }

    const groupByDate = () => {
        if (!schedules?.byDate) return [];

        return schedules?.byDate?.map(schedule => {
            let formattedScales = '';

            if(schedule.scale){
                let scales = schedule.scale.split("|");
                for(let i = 0; i < scales.length; i++){
                    if (i !== 0) formattedScales += ' | ';
                    formattedScales += translatedKeys[scales[i].trim()] ?? scales[i].trim();
                }
            }

            return{
                day: schedule.date,
                agents: schedule.effective,
                teamName: schedule.team,
                scales:  formattedScales
            };
        });
    }

    const exportData = async () => {
        const agGridData = getDataFromAgGrid();

        if (agGridData.length === 0){
            projectUtils.swalMessage('error', '', "Não há dados na tabela para exportação.", 'Ok')
            return;
        }

        let dataToExport = mapAgGridToSheet(agGridData);

        await generateAndPrintSheet(dataToExport);
    }

    const getDataFromAgGrid = () => {
        const filteredData = [];
        const rowCount = gridRef.current.api.getDisplayedRowCount();

        for (let i = 0; i < rowCount; i++) {
            const rowNode = gridRef.current.api.getDisplayedRowAtIndex(i);
            filteredData.push(rowNode.data);
        }

        return filteredData;
    }

    const mapAgGridToSheet = (data) => {
        if(data.length === 0) return [];

        if (selectedType === "effective") {
            return data?.map(schedule => {
                return {
                    "Efetivo": schedule.name,
                    "Jornada de trabalho": schedule.scale,
                    "Data": schedule.dates
                };
            });
        }

        if (selectedType === "team") {
            return data?.map(schedule => {
                return {
                    "Equipe": schedule.teamName,
                    "Jornada de trabalho": schedule.scale,
                    "Data": schedule.dates
                };
            });
        }

        if (selectedType === "day") {
            return data?.map(schedule => {
                return {
                    "Data": schedule.day,
                    "Equipe": schedule.teamName,
                    "Efetivo": schedule.agents,
                    "Jornada de trabalho": schedule.scales
                };
            });
        }
    }
    const generateAndPrintSheet = async (data) => {
        await exportObjectsToXlsx({
            fileName: `escala-${workSchedule?.name}.xlsx`,
            sheetName: "escala",
            data
        });
    }

    const openCalendar = () => {
        localStorage.setItem("work-schedule-start-date", JSON.stringify(workSchedule.startDate));
        navigate(`/work-schedule/calendar/${id}`, true);
    }

    const handleDelete = async () => {
        if(!id) return;

        projectUtils.swalMessage(
            'warning',
            'Excluir',
            `Excluir a escala "${workSchedule.name}"?`,
            'Sim',
            true,
            'Não',
            ({isConfirmed}) => {
                if(isConfirmed){
                    try {
                        workScheduleService.delete(id)
                        projectUtils.success(true, "Excluído com sucesso")
                        navigate('/work-schedule');
                    }
                    catch(error){
                        console.error(error)
                        let message = 'Não foi possível excluir.';
                        if (error.response.status !== 500) {
                            message = error.response.data.message;
                        }

                        projectUtils.swalErrorMessage('', message, 'Ok');
                    }
                }
            }
        );

    }

    return(
        <div className="flex flex-col gap-2">
            <Card>
                <Card.Header>
                    <Card.Title>Dados</Card.Title>
                </Card.Header>
                <Card.Body>
                    <div className="flex gap-2">
                        <div className="flex-[3]">
                            <InputForm id="group_name" type="text" label="Nome" value={workSchedule?.name ?? ''}
                                       disabled/>
                        </div>
                        <div className="flex-[1]">
                            <InputForm id="dt_ini" type="date" label="Data inicial"
                                       value={workSchedule?.startDate ?? ''} disabled/>
                        </div>
                        <div className="flex-[1]">
                            <InputForm id="dt_end" type="date" label="Data final" value={workSchedule?.endDate ?? ''}
                                       disabled/>
                        </div>
                    </div>
                </Card.Body>
            </Card>
            <Card>
                <Card.Body>
                    <div className="flex justify-between items-center">
                        <div className="flex flex-1 items-center gap-2">
                            <h5 className="text-[14.4px] text-[#676a6c] font-bold">Visualização da escala:</h5>
                            <div className="flex flex-1">
                                <div className={clsx(
                                    "rounded-l-[3px] -mr-px cursor-pointer bg-[#6c757d] text-white uppercase font-bold w-1/3 text-center py-[3px] border border-[#6c757d] hover:opacity-80",
                                    {
                                        "bg-[#ccd4de] !text-[#063a6c] border-[#ccd4de]": selectedType === "effective"
                                    }
                                )}
                                     onClick={() => setSelectedType('effective')}>
                                    Por efetivo
                                </div>
                                <div className={clsx(
                                    "cursor-pointer bg-[#6c757d] text-white uppercase font-bold w-1/3 text-center py-[3px] border border-[#6c757d] hover:opacity-80",
                                    {
                                        "bg-[#ccd4de] !text-[#063a6c] border-[#ccd4de]": selectedType === "day"
                                    }
                                )}
                                     onClick={() => setSelectedType('day')}>
                                    Por dia
                                </div>
                                <div className={clsx(
                                    "rounded-r-[3px] cursor-pointer bg-[#6c757d] text-white uppercase font-bold w-1/3 text-center py-[3px] border border-[#6c757d] hover:opacity-80",
                                    {
                                        "bg-[#ccd4de] !text-[#063a6c] border-[#ccd4de]": selectedType === "team"
                                    }
                                )}
                                     onClick={() => setSelectedType('team')}>
                                    Por equipe
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-1 justify-end items-center">
                            <Button target="secondary" title="Abrir no calendário" onClick={openCalendar}>
                                <FaCalendar/>
                            </Button>
                            <Button target="success" className="ms-2" onClick={exportData} title="Exportar" >
                                <FaFileExcel/>
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>
            <Card>
                <Card.Header>
                    <Card.Title>Escalas</Card.Title>
                </Card.Header>
                <Card.Body>
                    <div style={{height: "calc(100vh - 341px"}} >
                        <AgGridReact
                            ref={gridRef}
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme({rowHeight: 40, headerHeight: 40})}
                        />
                    </div>
                </Card.Body>
            </Card>
            <Card>
                <Card.Body>
                    <div className="flex w-full justify-end gap-2">
                        <Button target="danger" style={{marginRight:'5px'}} onClick={handleDelete} actionFeedbackType='delete'>
                            Excluir
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    )
}

export default WorkScheduleFormPresentation;